/*
  Variant picker + sizing chart (blocks/product_product-variant-picker-block.liquid,
  blocks/product_sizing-chart.liquid).

  <mv-variant-picker>  Listens for changes on its radio groups / selects and asks the
                       <mv-product> controller (assets/section-main-product.js) to re-render:
                         - normally `mv:option-change` { optionValues } (option value ids in
                           option order; the controller fetches `?option_values=`)
                         - when the chosen combination does not exist, or is sold out and the
                           block hides sold-out variants, it resolves the first existing
                           (available) variant that carries the value the customer clicked and
                           sends `mv:variant-select` { variantId } instead (the
                           skip-non-existent / skip-unavailable behaviour of the reference).
                       Every value list is a `data-mv-refresh` part, so the controller re-renders
                       it from the server; this element only restores focus afterwards (the
                       `mv:variant-change` handler is filtered by section id).
  <mv-sizing-chart>    Open button + dialog. The dialog node is moved to <body> on first open
                       (fixed positioning must not be trapped by transformed/sticky ancestors),
                       focus is trapped inside (Dawn's trapFocus when present), Esc / backdrop /
                       close button dismiss it, `data-test-mode` opens it on connect.
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };

  // ---- mv-variant-picker ----
  class MvVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this._onChange = (event) => this.handleChange(event);
      this._onVariantChange = (event) => this.handleVariantChange(event);
      this.addEventListener('change', this._onChange);
      document.addEventListener('mv:variant-change', this._onVariantChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('mv:variant-change', this._onVariantChange);
    }

    get controller() {
      return this.closest('mv-product');
    }

    get productData() {
      const state = MV.product && MV.product.getState ? MV.product.getState(this.sectionId) : null;
      return state ? state.product : null;
    }

    // Option groups in option order → { position, name, value, valueId, changed }
    readSelection(changedGroup) {
      return Array.from(this.querySelectorAll('.mv-variant-picker__option'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((group) => {
          const select = group.querySelector('select[data-mv-option-select]');
          const picked = select
            ? select.options[select.selectedIndex]
            : group.querySelector('input[type="radio"]:checked');
          return {
            position: Number(group.dataset.optionPosition),
            name: group.dataset.optionName,
            value: picked ? picked.value : null,
            valueId: picked && picked.dataset ? picked.dataset.optionValueId : null,
            changed: group === changedGroup,
          };
        });
    }

    handleChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches('input[type="radio"], select[data-mv-option-select]')) return;
      event.stopPropagation();
      const group = target.closest('.mv-variant-picker__option');
      const selection = this.readSelection(group);
      const values = selection.map((o) => o.value);
      if (values.some((v) => v == null)) return;

      this._focusId = target.id;
      this._hadFocus = document.activeElement === target;

      const product = this.productData;
      const exact = product ? this.findVariant(product, values) : null;
      const needsJump =
        product && (!exact || (this.skipUnavailable && !exact.available));

      if (needsJump) {
        const alternative = this.findAlternative(product, selection);
        if (alternative) {
          this.dispatchEvent(
            new CustomEvent('mv:variant-select', {
              bubbles: true,
              detail: { variantId: alternative.id, productUrl: this.dataset.productUrl },
            })
          );
          return;
        }
      }

      const optionValues = selection.map((o) => o.valueId);
      if (optionValues.every(Boolean)) {
        this.dispatchEvent(
          new CustomEvent('mv:option-change', {
            bubbles: true,
            detail: { optionValues, productUrl: this.dataset.productUrl },
          })
        );
      } else if (exact) {
        this.dispatchEvent(
          new CustomEvent('mv:variant-select', {
            bubbles: true,
            detail: { variantId: exact.id, productUrl: this.dataset.productUrl },
          })
        );
      }
    }

    findVariant(product, values) {
      return (product.variants || []).find((variant) =>
        values.every((value, i) => variant.options[i] === value)
      ) || null;
    }

    // First variant that keeps the value the customer just picked; prefers variants that also
    // keep the other current values, earlier options weighing more (Color before Size before
    // Pack), then any variant with that value.
    findAlternative(product, selection) {
      const variants = product.variants || [];
      const changed = selection.find((o) => o.changed) || selection[0];
      const changedIndex = selection.indexOf(changed);
      const usable = (variant) => (this.skipUnavailable ? variant.available : true);
      const candidates = variants.filter((v) => v.options[changedIndex] === changed.value && usable(v));
      if (!candidates.length) return null;
      const weight = (i) => 2 ** (selection.length - i);
      const score = (variant) =>
        selection.reduce((n, o, i) => (i !== changedIndex && variant.options[i] === o.value ? n + weight(i) : n), 0);
      return candidates.sort((a, b) => score(b) - score(a))[0];
    }

    handleVariantChange(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId) return;
      // The value lists were just re-rendered by the controller; put focus back where it was.
      if (this._hadFocus && this._focusId) {
        const input = this.querySelector(`#${CSS.escape(this._focusId)}`);
        if (input) input.focus({ preventScroll: true });
      }
      this._hadFocus = false;
    }
  }

  // ---- mv-sizing-chart ----
  class MvSizingChart extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-mv-open]');
      this.dialog = this.dialog || this.querySelector('.mv-sizing-chart__dialog');
      if (!this.button || !this.dialog) return;

      this._onOpen = () => this.open();
      this._onDialogClick = (event) => {
        // Close button, backdrop, or a click that lands on the dialog container itself (outside the panel).
        const target = event.target;
        if (target === this.dialog) return this.close();
        // Element, not HTMLElement: a tap on the X icon lands on its <svg>/<path> (SVGElement), which the old check ignored
        if (target instanceof Element && target.closest('[data-mv-close]')) this.close();
      };
      this._onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      };
      this.button.addEventListener('click', this._onOpen);
      this.dialog.addEventListener('click', this._onDialogClick);
      document.addEventListener('keydown', this._onKeydown);

      if (this.dataset.testMode === 'true') this.open();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onOpen);
      document.removeEventListener('keydown', this._onKeydown);
      // Block removed (editor) → take the detached dialog with it.
      if (this.dialog && this.dialog.parentElement === document.body) {
        if (this.isOpen) this.close();
        this.dialog.remove();
      }
    }

    get isOpen() {
      return this.dialog && !this.dialog.hidden;
    }

    open() {
      if (!this.dialog || this.isOpen) return;
      if (this.dialog.parentElement !== document.body) document.body.appendChild(this.dialog);
      this.dialog.hidden = false;
      requestAnimationFrame(() => this.dialog.classList.add('mv-sizing-chart__dialog--open'));
      document.body.classList.add('overflow-hidden');
      this.button.setAttribute('aria-expanded', 'true');
      const panel = this.dialog.querySelector('.mv-sizing-chart__panel');
      if (typeof window.trapFocus === 'function') window.trapFocus(this.dialog, panel);
      else if (panel) panel.focus();
      this.dispatchEvent(new CustomEvent('mv:sizing-chart-open', { bubbles: true, detail: { blockId: this.dataset.blockId } }));
    }

    close() {
      if (!this.isOpen) return;
      this.dialog.classList.remove('mv-sizing-chart__dialog--open');
      this.dialog.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this.button.setAttribute('aria-expanded', 'false');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.button);
      else if (this.isConnected) this.button.focus();
      this.dispatchEvent(new CustomEvent('mv:sizing-chart-close', { bubbles: true, detail: { blockId: this.dataset.blockId } }));
    }
  }

  define('mv-variant-picker', MvVariantPicker);
  define('mv-sizing-chart', MvSizingChart);
})();
